// Third-party
import React from 'react';
import Skeleton from 'react-skeleton-loader';
import moment from 'moment';

// Utils
import { FormattedMessage, formatMessage } from '../../../utils/i18n';

// Components
import { Button } from '@capintel/component-lib/button';
import { PrimaryLink } from '../../components/buttons';
import { SUBSCRIPTION_STATUS } from '../../../utils/3rd-party/chargebee';
import Subscription from '@/types/subscription';

type Props = {
  loading: boolean;
  onOpenCustomerPortal: () => void;
  onUpgradeToPro: () => void;
  onCancelSubscription: () => void;
  subscription?: Subscription;
};

enum PLAN {
  ESSENTIAL = 'ESSENTIAL',
  PRO_MONTHLY = 'PRO_MONTHLY',
  PRO_ANNUAL = 'PRO_ANNUAL',
}

const getNextBilling = (subscription: Subscription) => {
  if (subscription.nextBillingAt && subscription.nextBillingAmount) {
    return (
      <>
        <span className="next-billing">${`${(subscription.nextBillingAmount / 100).toFixed(2)}`}</span>

        <span>
          (
          <FormattedMessage id="Common.On" />
          {'\xa0'}
          {moment(subscription.nextBillingAt).format(formatMessage('Common.DateFormats.Short'))})
        </span>
      </>
    );
  }

  return formatMessage('Settings.Billing.NotApplicable');
};

const getPlanName = (subscription: Subscription) =>
  // If we have a translation defined for that plan, use it - else, use the non-localized name from Chargebee.
  formatMessage(`Settings.Billing.Plans.${subscription.plan}`, undefined, subscription.planName);
const SettingsBilling = ({ onOpenCustomerPortal, onCancelSubscription, onUpgradeToPro, subscription, loading }: Props) => (
  <div className="settings-billing settings-tab">
    <h3>
      <FormattedMessage id="Settings.Billing.Title" />
    </h3>

    {loading && <Skeleton count={2} />}

    {!loading && !subscription && (
      <p>
        <FormattedMessage id="Settings.Billing.NoCbAccount" />
      </p>
    )}

    {subscription && (
      <>
        <p>
          <label>
            <FormattedMessage id="Settings.Billing.Plan" />
          </label>

          <span className="plan-name">{getPlanName(subscription)}</span>

          {subscription.status === SUBSCRIPTION_STATUS.IN_TRIAL && (
            <span className="trial-end">
              <FormattedMessage
                id="Settings.Billing.TrialEnds"
                values={{
                  trialEnd: moment(subscription.trialEnd).format(formatMessage('Common.DateFormats.Short')),
                }}
              />
            </span>
          )}
        </p>

        <p>
          <label>
            <FormattedMessage id="Settings.Billing.NextBilling" />
          </label>

          {getNextBilling(subscription)}
        </p>

        <p className="delay-disclaimer">
          <FormattedMessage id="Settings.Billing.DelayDisclaimer" />
        </p>

        <div className="billing-actions">
          {subscription.plan === PLAN.ESSENTIAL && (
            <Button variant="primary" onClick={onUpgradeToPro}>
              <FormattedMessage id="Settings.Billing.UpgradeToPro" />
            </Button>
          )}

          <Button variant="secondary" onClick={onOpenCustomerPortal}>
            <FormattedMessage id="Settings.Billing.ManageAccount" />
          </Button>

          {subscription.plan !== PLAN.ESSENTIAL && (
            <PrimaryLink onClick={onCancelSubscription}>
              <FormattedMessage id="Settings.Billing.CancelSubscription" />
            </PrimaryLink>
          )}
        </div>
      </>
    )}
  </div>
);

export default SettingsBilling;
